/**
 * extractModel.ts
 *
 * Pure helpers for Extract mode (DD and BA): turn the stored `extractedFields`
 * of a file's documents into a flat field list, resolve which fields are
 * confirmed, and build the payload for `finalizeDocumentReview`.
 *
 * No React and no network. Field parsing mirrors `pages/FileReview.tsx`.
 *
 * Requirements: 7.3, 7.4, 7.5, 7.6.
 */

import type { Document, File as FileType, Loan } from '../../graphql/types';
import { normalizeBoundingBox, sortInReadingOrder } from '../../components/documentViewerGeometry';
import type { BoundingBox } from '../../components/documentViewerGeometry';
import { CONFIDENCE_THRESHOLD } from '../../components/ui/ConfidencePill';

/** "3" -> 3..3, "2-5" -> 2..5, anything else -> null. */
export function parsePageRange(range: string | undefined): { start: number; end: number } | null {
  const m = /^\s*(\d+)\s*(?:-\s*(\d+))?\s*$/.exec(range ?? '');
  if (!m) return null;
  const start = parseInt(m[1], 10);
  const end = m[2] ? parseInt(m[2], 10) : start;
  return start >= 1 && end >= start ? { start, end } : null;
}

export interface ExtractField {
  /** Stable key: `${documentId}:${fieldName}` (suffixed `#n` for repeats). Used in the URL. */
  id: string;
  documentId: string;
  /** Raw field name, for example `borrower_name`. */
  name: string;
  /** Display label, for example `borrower name`. */
  label: string;
  value: string;
  confidence: number;
  /** 1-based absolute PDF page. */
  page: number;
  boundingBox: BoundingBox | null;
  /** Source document, for example "Deed of Trust". */
  documentLabel: string;
  pageRange: string;
  /** Set when the extraction data says this value conflicts with another document. */
  conflictNote?: string;
  /** Value as originally extracted, kept when the reviewer edited the field. */
  extractedValue?: string;
}

/** Explicit per-field choice made by the reviewer; absent keys use the default. */
export type ConfirmationOverrides = Record<string, boolean>;

/** The reviewer's edited value for a field, keyed by field id. */
export type FieldEdits = Record<string, string>;

export interface DocumentFieldGroup {
  documentId: string;
  documentLabel: string;
  pageRange: string;
  fields: ExtractField[];
}

export function makeFieldId(documentId: string, name: string): string {
  return `${documentId}:${name}`;
}

function startPage(pageRange: string | undefined): number {
  const n = parseInt(pageRange?.split('-')[0] || '1', 10);
  return Number.isFinite(n) ? n : 1;
}

/** Documents in page order, as the legacy file review lists them. */
export function sortDocuments(documents: ReadonlyArray<Document>): Document[] {
  return [...documents].sort((a, b) => startPage(a.pageRange) - startPage(b.pageRange));
}

function readString(raw: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string' && v.trim()) return v;
  }
  return undefined;
}

function parseDocumentFields(doc: Document): ExtractField[] {
  let parsed: unknown;
  try {
    parsed = doc.extractedFields ? JSON.parse(doc.extractedFields) : [];
  } catch {
    parsed = [];
  }
  if (!Array.isArray(parsed)) return [];

  const seen = new Map<string, number>();
  const result: ExtractField[] = [];
  for (const entry of parsed) {
    if (!entry || typeof entry !== 'object') continue;
    const raw = entry as Record<string, unknown>;
    const name = String(raw.field_name ?? raw.field ?? '');
    const valueRaw = raw.field_value ?? raw.value ?? '';
    const confidenceRaw = Number(raw.confidence_score ?? raw.confidence ?? 0);
    const pageRaw = Number(raw.page ?? 1);

    const count = seen.get(name) ?? 0;
    seen.set(name, count + 1);
    const baseId = makeFieldId(doc.documentId, name);

    const storedExtractedRaw = raw.extracted_value ?? raw.extractedValue;
    const storedExtracted =
      storedExtractedRaw == null ? undefined : String(storedExtractedRaw);
    const field: ExtractField = {
      id: count === 0 ? baseId : `${baseId}#${count + 1}`,
      documentId: doc.documentId,
      name,
      label: name.replace(/_/g, ' '),
      value: valueRaw == null ? '' : String(valueRaw),
      confidence: Number.isFinite(confidenceRaw) ? confidenceRaw : 0,
      page: Number.isFinite(pageRaw) && pageRaw >= 1 ? Math.round(pageRaw) : 1,
      boundingBox: normalizeBoundingBox(raw.bounding_box ?? raw.boundingBox ?? null),
      documentLabel: doc.documentType || 'Document',
      pageRange: doc.pageRange || '',
      conflictNote: readString(raw, 'conflict_note', 'conflictNote'),
    };
    if (storedExtracted !== undefined && storedExtracted !== field.value) {
      field.extractedValue = storedExtracted;
    }
    result.push(field);
  }
  // Ids are assigned above, so ordering the list by position leaves them stable.
  return sortInReadingOrder(result, (f) => f);
}

/** Flat field list for a file, grouped by document in page order. */
export function groupExtractFields(documents: ReadonlyArray<Document>): DocumentFieldGroup[] {
  return sortDocuments(documents).map((doc) => ({
    documentId: doc.documentId,
    documentLabel: doc.documentType || 'Document',
    pageRange: doc.pageRange || '',
    fields: parseDocumentFields(doc),
  }));
}

export function flattenGroups(groups: ReadonlyArray<DocumentFieldGroup>): ExtractField[] {
  return groups.flatMap((g) => g.fields);
}

/** True when the value was read with low confidence. */
export function isLowConfidence(
  field: Pick<ExtractField, 'confidence'>,
  threshold = CONFIDENCE_THRESHOLD
): boolean {
  return !(field.confidence >= threshold);
}

/**
 * Whether a field starts confirmed. Values read with high confidence and no
 * conflict are confirmed by default (as the legacy file review accepted them);
 * low-confidence and conflicting values wait for the reviewer.
 */
export function defaultConfirmed(field: ExtractField, threshold = CONFIDENCE_THRESHOLD): boolean {
  return !isLowConfidence(field, threshold) && !field.conflictNote;
}

export function isConfirmed(
  field: ExtractField,
  overrides: ConfirmationOverrides,
  threshold = CONFIDENCE_THRESHOLD
): boolean {
  const explicit = overrides[field.id];
  return explicit === undefined ? defaultConfirmed(field, threshold) : explicit;
}

export function countConfirmed(
  fields: ReadonlyArray<ExtractField>,
  overrides: ConfirmationOverrides,
  threshold = CONFIDENCE_THRESHOLD
): number {
  return fields.filter((f) => isConfirmed(f, overrides, threshold)).length;
}

/** Plain-language reason a field needs attention, or undefined when it does not. */
export function fieldNote(
  field: ExtractField,
  threshold = CONFIDENCE_THRESHOLD
): string | undefined {
  if (field.conflictNote) return field.conflictNote;
  if (isLowConfidence(field, threshold)) {
    return `Read with low confidence (${Math.round(field.confidence * 100)}%). Check the value against page ${field.page}.`;
  }
  return undefined;
}

/** Return `overrides` with one field set; keys equal to the default are dropped. */
export function setConfirmation(
  overrides: ConfirmationOverrides,
  field: ExtractField,
  confirmed: boolean
): ConfirmationOverrides {
  const next = { ...overrides };
  if (confirmed === defaultConfirmed(field)) delete next[field.id];
  else next[field.id] = confirmed;
  return next;
}

/** Field resolved from a URL `field` value: exact id first, then bare field name. */
export function findFieldByParam(
  fields: ReadonlyArray<ExtractField>,
  param: string | undefined | null
): ExtractField | undefined {
  if (!param) return undefined;
  return fields.find((f) => f.id === param) ?? fields.find((f) => f.name === param);
}

export interface FinalizeFieldEntry {
  field: string;
  value: string;
  confidence: number;
  /** 1-based absolute PDF page the value was read from. */
  page: number;
  /** Where the value sits on that page, as fractions of the page. */
  bounding_box?: BoundingBox;
  /** The value as originally extracted, when the reviewer edited the field. */
  extracted_value?: string;
}

/**
 * Payload for `finalizeDocumentReview`: fields grouped by document id.
 *
 * The mutation replaces each document's stored `extracted_fields` with exactly
 * what is sent, so every entry carries the `page` and `bounding_box` it was
 * read with. Without them a saved field comes back from GraphQL with no
 * location, and Extract can no longer highlight its source on the page.
 *
 * `edits` carries the reviewer's edited value per field; the stored
 * `extracted_value` keeps the original visible on a reopen.
 */
export function buildFinalizePayload(
  groups: ReadonlyArray<DocumentFieldGroup>,
  edits: FieldEdits = {}
): Record<string, FinalizeFieldEntry[]> {
  const payload: Record<string, FinalizeFieldEntry[]> = {};
  for (const g of groups) {
    payload[g.documentId] = g.fields.map((f) => {
      const edited = Object.prototype.hasOwnProperty.call(edits, f.id);
      const value = edited ? edits[f.id] : f.value;
      const entry: FinalizeFieldEntry = {
        field: f.name,
        value,
        // Edits are entered by the reviewer, so they are known-good.
        confidence: edited ? 1 : f.confidence,
        page: f.page,
      };
      if (f.boundingBox) entry.bounding_box = f.boundingBox;
      if (edited) entry.extracted_value = f.extractedValue ?? f.value;
      else if (f.extractedValue !== undefined) entry.extracted_value = f.extractedValue;
      return entry;
    });
  }
  return payload;
}

// ── Which file Extract shows ────────────────────────────────────────────────

export interface ExtractSelectionInput {
  loans: ReadonlyArray<Loan>;
  files: ReadonlyArray<FileType>;
  documents: ReadonlyArray<Document>;
}

export interface ExtractSelectionParams {
  loan?: string;
  doc?: string;
  field?: string;
}

export interface ExtractSelection {
  /** The loan shown, or null when the job has no loans. */
  loanId: string | null;
  /** The file shown in the viewer, or null when the loan has nothing extracted. */
  file: FileType | null;
  /** Documents of `file`. */
  documents: Document[];
  /** Files of the loan that have documents, for the file switcher. */
  loanFiles: FileType[];
  /** All documents of the loan, across its files. */
  loanDocuments: Document[];
}

/**
 * Pick the loan, file and documents Extract shows from the URL params.
 *
 * Loan: the `loan` param when it names a loan of the job; else the loan that
 * owns the `doc` (or the `field`'s document); else the first loan with
 * extracted documents; else the first loan.
 * File: the file of the `doc` (or `field`'s document) inside that loan; else
 * the loan's first file that has documents.
 */
export function resolveExtractSelection(
  input: ExtractSelectionInput,
  params: ExtractSelectionParams
): ExtractSelection {
  const { loans, files, documents } = input;

  const docIdFromField = params.field ? params.field.split(':')[0] : undefined;
  const requestedDoc =
    documents.find((d) => d.documentId === params.doc) ??
    documents.find((d) => d.documentId === docIdFromField);

  const explicitLoan = loans.find((l) => l.loanId === params.loan);
  const loan =
    explicitLoan ??
    loans.find((l) => l.loanId === requestedDoc?.loanId) ??
    loans.find((l) => documents.some((d) => d.loanId === l.loanId)) ??
    loans[0];
  if (!loan) return { loanId: null, file: null, documents: [], loanFiles: [], loanDocuments: [] };

  const loanDocuments = documents.filter((d) => d.loanId === loan.loanId);
  const loanFiles = files.filter(
    (f) => f.loanId === loan.loanId && loanDocuments.some((d) => d.fileId === f.fileId)
  );
  const wanted = requestedDoc && requestedDoc.loanId === loan.loanId ? requestedDoc : undefined;
  const file = loanFiles.find((f) => f.fileId === wanted?.fileId) ?? loanFiles[0] ?? null;

  return {
    loanId: loan.loanId,
    file,
    documents: file ? loanDocuments.filter((d) => d.fileId === file.fileId) : [],
    loanFiles,
    loanDocuments,
  };
}

export interface LoanFieldSummary {
  total: number;
  confirmed: number;
  /** Fields read with low confidence that the reviewer has not confirmed. */
  lowConfidence: number;
}

/** Field counts for one loan, using the confirmations saved for it. */
export function summarizeLoanFields(
  jobId: string,
  loanId: string,
  documents: ReadonlyArray<Document>,
  storage?: StorageLike | null
): LoanFieldSummary {
  const fields = flattenGroups(groupExtractFields(documents));
  const overrides = loadConfirmations(jobId, loanId, storage);
  return {
    total: fields.length,
    confirmed: countConfirmed(fields, overrides),
    lowConfidence: fields.filter((f) => isLowConfidence(f) && !isConfirmed(f, overrides)).length,
  };
}

// ── Persistence (per loan) ──────────────────────────────────────────────────

export function confirmationStorageKey(jobId: string, loanId: string): string {
  return `docmind:extract-confirmations:${jobId}:${loanId}`;
}

export function editsStorageKey(jobId: string, loanId: string): string {
  return `docmind:extract-edits:${jobId}:${loanId}`;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/**
 * Confirmations are kept per loan on this device until the backend can store a
 * confirmation without an edit (design.md, Backend gaps 3). "Continue to
 * Analyze" also writes the file's fields through `finalizeDocumentReview`.
 */
export function loadConfirmations(
  jobId: string,
  loanId: string,
  storage: StorageLike | null = defaultStorage()
): ConfirmationOverrides {
  if (!storage) return {};
  try {
    const raw = storage.getItem(confirmationStorageKey(jobId, loanId));
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const result: ConfirmationOverrides = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'boolean') result[k] = v;
    }
    return result;
  } catch {
    return {};
  }
}

export function saveConfirmations(
  jobId: string,
  loanId: string,
  overrides: ConfirmationOverrides,
  storage: StorageLike | null = defaultStorage()
): void {
  if (!storage) return;
  try {
    storage.setItem(confirmationStorageKey(jobId, loanId), JSON.stringify(overrides));
  } catch {
    // Storage full or blocked: confirmations stay in memory for this session.
  }
}

/** Reviewer edits kept per loan, same lifetime as confirmations. */
export function loadEdits(
  jobId: string,
  loanId: string,
  storage: StorageLike | null = defaultStorage()
): FieldEdits {
  if (!storage) return {};
  try {
    const raw = storage.getItem(editsStorageKey(jobId, loanId));
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const result: FieldEdits = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'string') result[k] = v;
    }
    return result;
  } catch {
    return {};
  }
}

export function saveEdits(
  jobId: string,
  loanId: string,
  edits: FieldEdits,
  storage: StorageLike | null = defaultStorage()
): void {
  if (!storage) return;
  try {
    storage.setItem(editsStorageKey(jobId, loanId), JSON.stringify(edits));
  } catch {
    // Edits stay in memory for this session if storage is unavailable.
  }
}
